/**
 * Shared TSS / intensity estimation for the dashboard charts.
 *
 * Structured workouts (warm-up + an "Nx" repeat block + cooldown) span many
 * steps. The naïve "first duration token" parse only saw the warm-up, so the
 * TSS chart and the intensity chart badly under-counted planned quality/interval
 * sessions. These reuse the real segment parser (parseWorkoutSegments /
 * parseStructuredWorkout, the same one the calendar uses) for multi-line
 * descriptions, and fall back to keyword estimation only for single-line ones.
 */
import { CalendarEvent } from "../types/training";
import { parseStructuredWorkout, parseWorkoutSegments } from "./planGenerator";

const HARD_KEYWORDS = /interval|intervall|threshold|tröskel|z4|z5|sprint|fartlek|backe|hill/i;
const EASY_OVERRIDE = /pendling|commute|lätt|easy|lugn|vila|recovery/i;

/** Hours from "1h30m", "1h 30min", "40m", "90 min", "1.5h". The lookbehind
 *  excludes a preceding letter OR digit so "Z2 HR" isn't read as hours and the
 *  "0m" inside "30m" isn't read as 0 minutes. The hours match does NOT require a
 *  word boundary after "h" so the compact intervals.icu form "1h30m" parses. */
export function parseDurationHours(text: string): number {
  const t = text.toLowerCase();
  const h = t.match(/(?<![a-z\d])(\d+(?:[.,]\d+)?)\s*h(?:ours?)?/);
  if (h) {
    const rest = t.slice(t.indexOf(h[0]) + h[0].length);
    const m2 = rest.match(/^\s*(\d+)\s*m(?:in)?/);
    return parseFloat(h[1].replace(",", ".")) + (m2 ? parseInt(m2[1]) / 60 : 0);
  }
  const m = t.match(/(?<![a-z\d])(\d+)\s*m(?:in)?\b/);
  if (m) return parseInt(m[1]) / 60;
  return 0;
}

/** The bar height for a week in the TSS chart: a past week shows its actual, a
 *  future week its planned, the current week actual + planned STACKED (so it's
 *  comparable in height to a completed week instead of showing only the taller). */
export function weekBarTotal(actual: number, planned: number, isPast: boolean): number {
  return actual + (planned > 0 && !isPast ? planned : 0);
}

/** IF by zone/keyword + sport. TSS = hours × IF² × 100. Used only for single-line events. */
function intensityFactor(text: string, sportType?: string | null): number {
  const t = text.toLowerCase();
  const isRun = /^run$/i.test(sportType ?? "");
  const isRide = /ride/i.test(sportType ?? "");

  if (isRun) {
    if (/z1|recovery|vila|lätt|easy/.test(t)) return 0.68;
    if (/z5|sprint|max/.test(t)) return 0.95;
    if (/z4|tröskel|threshold|intervall|interval|fartlek/.test(t)) return 0.90;
    if (/z3|marathon|maraton|tempo/.test(t)) return 0.85;
    if (/z2|hr\b|heartrate|aerob/.test(t)) return 0.81;
    return 0.78;
  }
  if (isRide) {
    if (/pendling|commute/.test(t)) return 0.60;
    if (/z1|recovery|vila|lätt|easy/.test(t)) return 0.65;
    if (/z5|sprint|max/.test(t)) return 0.90;
    if (/z4|tröskel|threshold|intervall|interval|fartlek/.test(t)) return 0.84;
    if (/z3|marathon|maraton|tempo/.test(t)) return 0.77;
    if (/z2|hr\b|heartrate|aerob/.test(t)) return 0.71;
    return 0.68;
  }
  if (/pendling|commute/.test(t)) return 0.63;
  if (/z1|recovery|vila|lätt|easy/.test(t)) return 0.68;
  if (/z5|sprint|max/.test(t)) return 0.93;
  if (/z4|tröskel|threshold|intervall|interval|fartlek/.test(t)) return 0.87;
  if (/z3|marathon|maraton|tempo/.test(t)) return 0.81;
  if (/z2|hr\b|heartrate|aerob/.test(t)) return 0.75;
  return 0.71;
}

/** Estimated TSS for a planned event. Structured workouts are summed across all
 *  steps; single-line descriptions fall back to duration × IF². */
export function estimateEventTSS(event: CalendarEvent): number {
  if (event.load && event.load > 0) return event.load; // explicit TSS from intervals.icu wins
  const desc = event.description ?? "";
  if (desc.includes("\n")) {
    const parsed = parseStructuredWorkout(desc);
    if (parsed) return parsed.tss;
  }
  const text = `${event.name ?? ""} ${desc}`;
  const hours = parseDurationHours(text);
  if (hours <= 0) return 0;
  const IF = intensityFactor(text, event.type);
  return Math.round(hours * IF * IF * 100);
}

/** Hard (Z4+) and total minutes for a planned event. Structured workouts use
 *  per-segment zone time; single-line descriptions use the keyword heuristic. */
export function eventHardTotalMins(event: CalendarEvent): { hard: number; total: number } {
  const desc = event.description ?? "";
  if (desc.includes("\n")) {
    const segs = parseWorkoutSegments(desc);
    if (segs.length > 0) {
      const total = segs.reduce((s, x) => s + x.min, 0);
      const hard = segs.filter(x => x.zone >= 4).reduce((s, x) => s + x.min, 0);
      if (total > 0) return { hard, total };
    }
  }
  const text = `${event.name ?? ""} ${desc}`;
  const hours = parseDurationHours(text);
  if (hours <= 0) return { hard: 0, total: 0 };
  const mins = hours * 60;
  const isHard = HARD_KEYWORDS.test(text) && !EASY_OVERRIDE.test(text);
  return { hard: isHard ? mins : 0, total: mins };
}
